// AiFeedbackPanel — the AI feedback loop (Settings → AI Connections).
//
// Top: each AI job's last 7 days — how often its output was used unchanged and
// which fields people fix most. A job with no capture point says so, rather
// than reading as a job that is never wrong.
//
// Below: the weekly review's proposed rule changes (Wednesdays at noon, or
// "Run review now"). Approving one does NOT change a prompt — prompts are files
// in the repository under code review — so an approval shows the patch to put
// in a pull request, and the eval cases to add beside it. Every figure and
// every sentence comes from the server; the panel only lays them out.
import { useState } from 'react';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { fmt } from '../../utils/format';
import { Btn, Spinner, ErrorMsg, Card, Badge, useToast } from '../ui';

const cell = { padding: '6px 8px', borderBottom: `1px solid ${B.border}`, fontSize: 13, textAlign: 'left', verticalAlign: 'top' };

function copyText(text, toast) {
  navigator.clipboard?.writeText(text).then(() => toast.success('Copied.'), () => toast.error('Could not copy.'));
}

function JobTable({ jobs }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
      <thead>
        <tr style={{ color: B.textMd }}>
          <th style={cell}>Job</th><th style={cell}>Decisions</th><th style={cell}>Corrected</th>
          <th style={cell}>Used unchanged</th><th style={cell}>Most-fixed fields</th>
        </tr>
      </thead>
      <tbody>
        {jobs.map(j => (
          <tr key={j.job}>
            <td style={cell}>{j.label}{!j.captured && <div style={{ fontSize: 12, color: B.textLt }}>No capture point yet</div>}</td>
            <td style={cell}>{j.captured ? j.total : '—'}</td>
            <td style={cell}>{j.captured ? j.corrections : '—'}</td>
            <td style={cell}>{j.acceptedUnchangedRate == null ? '—' : `${j.acceptedUnchangedRate}%`}</td>
            <td style={cell}>{(j.topFields || []).map(f => `${f.field} (${f.count})`).join(', ') || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Proposal({ p, onDecided }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [patch, setPatch] = useState(null);

  const decide = async (decision) => {
    setBusy(true);
    try {
      const r = await api.post(`ai-rule-proposals/${p.proposalId}/decide`, { decision });
      if (r.patch) setPatch(r.patch);
      toast.success(decision === 'approve' ? 'Approved — apply the patch in a pull request.' : 'Rejected.');
      onDecided();
    } catch (e) { toast.error(e.message || 'Could not save the decision.'); }
    finally { setBusy(false); }
  };

  const showPatch = async () => {
    try { const r = await api.get(`ai-rule-proposals/${p.proposalId}/patch`); setPatch(r.patch || ''); }
    catch (e) { toast.error(e.message || 'Could not load the patch.'); }
  };

  return (
    <div style={{ border: `1px solid ${B.border}`, borderRadius: 8, padding: 12, marginBottom: 10 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <strong style={{ color: B.text }}>{p.title}</strong>
        <Badge color={B.textMd}>{p.jobLabel}</Badge>
        <Badge color={p.status === 'approved' ? B.green : p.status === 'rejected' ? B.textLt : B.amber}>{p.status}</Badge>
        <span style={{ fontSize: 12, color: B.textLt }}>week of {fmt.date(p.weekOf)} · {p.evidence.length} supporting correction{p.evidence.length === 1 ? '' : 's'}</span>
      </div>
      <div style={{ marginTop: 6, color: B.text }}>{p.rule}</div>
      {p.rationale && <div style={{ marginTop: 4, fontSize: 13, color: B.textMd }}>{p.rationale}</div>}
      {p.targetFile && <div style={{ marginTop: 4, fontSize: 12, color: B.textLt }}>Edits <code>{p.targetFile}</code></div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        {p.status === 'pending' && <Btn size="sm" disabled={busy} onClick={() => decide('approve')}>Approve</Btn>}
        {p.status === 'pending' && <Btn size="sm" variant="secondary" disabled={busy} onClick={() => decide('reject')}>Reject</Btn>}
        {p.status === 'approved' && !patch && <Btn size="sm" variant="secondary" onClick={showPatch}>Show patch</Btn>}
        {p.evalCases.length > 0 && (
          <Btn size="sm" variant="secondary" onClick={() => copyText(JSON.stringify(p.evalCases, null, 2), toast)}>
            Copy {p.evalCases.length} eval case{p.evalCases.length === 1 ? '' : 's'}
          </Btn>
        )}
      </div>
      {patch !== null && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 12, color: B.textMd, marginBottom: 4 }}>
            Put this in a pull request. The prompt changes when the PR merges, after its evals pass.
          </div>
          <pre style={{ background: B.surfaceMuted, padding: 8, borderRadius: 6, fontSize: 12, overflowX: 'auto', margin: 0 }}>{patch || 'No patch — the prompt file could not be read.'}</pre>
          {patch && <Btn size="sm" variant="secondary" style={{ marginTop: 6 }} onClick={() => copyText(patch, toast)}>Copy patch</Btn>}
        </div>
      )}
    </div>
  );
}

export function AiFeedbackPanel() {
  const { toast } = useToast();
  const summary = useApi(() => api.get('ai-corrections/summary?days=7'), []);
  const proposals = useApi(() => api.get('ai-rule-proposals'), []);
  const [running, setRunning] = useState(false);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post('ai-rule-proposals/review', {}, { timeout: 9 * 60 * 1000 });
      const n = (r.jobs || []).reduce((s, j) => s + (j.proposals?.length || 0), 0);
      toast.success(r.reason || (n ? `Drafted ${n} rule change${n === 1 ? '' : 's'}.` : 'No new corrections to review.'));
      proposals.reload();
      summary.reload();
    } catch (e) { toast.error(e.message || 'The review did not finish.'); }
    finally { setRunning(false); }
  };

  const list = proposals.data?.proposals || [];
  const pending = list.filter(p => p.status === 'pending');
  const decided = list.filter(p => p.status !== 'pending').slice(0, 10);

  return (
    <Card style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <div style={{ fontWeight: 600, color: B.text }}>AI feedback</div>
        <Btn size="sm" disabled={running} onClick={runNow}>{running ? 'Reviewing…' : 'Run review now'}</Btn>
      </div>
      <div style={{ fontSize: 13, color: B.textMd, marginBottom: 10 }}>
        When someone moves a document, edits a suggested task, re-routes a note or fixes a journal entry,
        that correction is saved. Every Wednesday at noon the week's corrections are reviewed and rule
        changes are drafted for each job's instructions. Approving a change gives you a patch for a pull
        request; nothing changes until it is merged.
        {summary.data && summary.data.reviewEnabled === false && <strong> The weekly review is paused in settings.</strong>}
      </div>

      {summary.loading ? <Spinner /> : summary.error ? <ErrorMsg msg={summary.error} /> : (
        <>
          {summary.data?.available === false && <div style={{ color: B.amber, fontSize: 13, marginBottom: 8 }}>{summary.data.reason}</div>}
          <JobTable jobs={summary.data?.jobs || []} />
        </>
      )}

      <div style={{ fontWeight: 600, color: B.text, margin: '8px 0' }}>Proposed rule changes</div>
      {proposals.loading ? <Spinner /> : proposals.error ? <ErrorMsg msg={proposals.error} /> : (
        <>
          {!pending.length && <div style={{ fontSize: 13, color: B.textLt, marginBottom: 8 }}>Nothing waiting for a decision.</div>}
          {pending.map(p => <Proposal key={p.proposalId} p={p} onDecided={proposals.reload} />)}
          {decided.length > 0 && <div style={{ fontSize: 13, color: B.textMd, margin: '8px 0 6px' }}>Recently decided</div>}
          {decided.map(p => <Proposal key={p.proposalId} p={p} onDecided={proposals.reload} />)}
        </>
      )}
    </Card>
  );
}
